import { Box } from '@mui/material';
import { useEntityFromRoute } from '@/common/hooks/route/useEntityFromRoute';
import { useNavState } from '../../navState/NavStateContext';
import { EntityProvider } from '../EntityProvider';
import { EntityDetail } from '../entityDetail/EntityDetail';
import { EntityList } from '../entityList/EntityList';
import { Mode } from '../enumerations/Mode';
import { EntityMgrProvider } from './EntityMgrContext';

/**
 * EntityView controls the display of List, Details, and Perspectives based on the mode (navState) and entity (route)
 * For shared components (base product), EntityView works with EntityProvider to automatically pick up and provide
 * these UI components similar to Java reflection (ex: If CtrtList exists, it will automatically be leveraged).
 * 
 * If a custom component is not found, the default EntityList or EntityDetail will be rendered, leveraging default
 * fieldDefs from the backend and default functionality.
 * 
 * EntityView also supports providing List, Detail, and Perspective components directly as props. This should only need to be done
 * in the scenario of org-specific UIs, as they will not be automatically picked up by EntityProvider. This is a design decision
 * made to simplify the implementation of org-specific UIs. 
 */
export const EntityView = ({
  listComponent: listComponentProp,
  detailComponent: detailComponentProp,
  perspectiveComponent: perspectiveComponentProp
}) => {

  const { mode } = useNavState();
  const entity = useEntityFromRoute();

  const ListComponent = listComponentProp ?? EntityProvider.list(entity) ?? EntityList;
  const DetailComponent = detailComponentProp ?? EntityProvider.detail(entity) ?? EntityDetail;
  const PerspectiveComponent = perspectiveComponentProp ?? EntityProvider.perspective(entity) ?? Box; // Currently, there is no default for Perspectives due to the need to provide Views

  const listComponent = <ListComponent />
  const detailComponent = <DetailComponent />
  const perspectiveComponent = <PerspectiveComponent />

  return (
    <EntityMgrProvider listComponent={listComponent}>

      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* List */}
        {mode === Mode.LIST && listComponent}

        {/* Detail */}
        {mode === Mode.DETAIL && detailComponent}

        {/* Perspective */}
        {mode === Mode.PERSPECTIVE && perspectiveComponent}

      </Box>

    </EntityMgrProvider>
  );
};